import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

/**
 * Query hooks for the Virtual Tours admin surface —
 * `GET /api/admin/virtual-tours/*` (ff-api, Matterport carve-out, Phase 1).
 *
 * Admin routes answer with bare `jsonify` bodies, same shape as
 * `/api/admin/claims` — tolerate a `{status, data}` envelope too, same as
 * the ownership-claims client.
 */

const BASE = '/api/admin/virtual-tours';

const unwrap = (raw: unknown): any =>
  raw != null && typeof raw === 'object' && 'data' in (raw as object) && 'status' in (raw as object)
    ? (raw as { data: unknown }).data
    : raw;

const buildQs = (params?: Record<string, unknown>): string => {
  if (!params) return '';
  const clean = Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type VirtualTourStatus =
  | 'processing'
  | 'active'
  | 'inactive'
  | 'archived'
  | 'failed'
  | 'quota_blocked'
  | 'unlinked';

export type VirtualTourSource = 'webhook' | 'reconcile' | 'dispatch' | 'admin' | 'admin_bypass' | 'seller' | 'backfill';

export interface VirtualTourRun {
  run_id: string;
  provider: string;
  triggered_by: string | null;
  actor_uid: number | null;
  status: string;
  since_watermark: string | null;
  new_watermark: string | null;
  scanned: number | null;
  linked: number | null;
  quota_blocked: number | null;
  failed: number | null;
  repaired: number | null;
  error: string | null;
  summary: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface VirtualTourProviderInfo {
  label: string;
  api: boolean;
  capabilities: string[];
}

export interface VirtualTourOverview {
  by_status: Record<string, number>;
  by_provider: Record<string, number>;
  active_on_non_live_listings: number;
  linked_without_asset: number;
  without_eligible_plan?: number;
  linked_by_bypass?: number;
  errors_24h: number;
  last_run: VirtualTourRun | null;
  providers: Record<string, VirtualTourProviderInfo>;
}

export interface VirtualTour {
  vtid: number;
  eid: number | null;
  asid: number | null;
  provider: string;
  external_id: string | null;
  share_url: string | null;
  status: VirtualTourStatus;
  visibility: string | null;
  source: VirtualTourSource;
  last_synced_at: string | null;
  last_error: string | null;
  date_added: string | null;
  listing_name: string | null;
  listing_uuid: string | null;
  etid: number | null;
  tester: number | null;
  listing_live: 0 | 1;
  /** false = no plan/promotion with a tour; null = n/a or unknown. */
  plan_eligible?: boolean | null;
  allowance?: VirtualTourPlanAllowance | null;
}

export interface VirtualTourPlanAllowance {
  /** -1 = unlimited. */
  limit: number;
  used: number;
  allowance_source: 'base' | 'plan' | 'addon' | 'promotion' | string;
  eligible: boolean;
}

export interface VirtualTourEvent {
  id: number;
  vtid: number | null;
  eid: number | null;
  provider: string | null;
  external_id: string | null;
  op: string;
  outcome: 'ok' | 'error' | 'blocked';
  error_code: string | null;
  message: string | null;
  actor_uid: number | null;
  run_id: string | null;
  created_at: string | null;
}

export interface VirtualTourDetail extends VirtualTour {
  embed_options: Record<string, unknown> | null;
  meta: Record<string, unknown> | null;
  dispatch_uuid: string | null;
  srid: number | null;
  date_modified: string | null;
  capabilities: string[];
  events: VirtualTourEvent[];
}

export interface VirtualTourModel {
  id: string;
  name: string | null;
  internalId: string | null;
  created: string | null;
  state: string | null;
  visibility?: string | null;
  accessVisibility?: string | null;
  vtid: number | null;
  tour_status: string | null;
  eid: number | null;
  listing_name: string | null;
  listing_live: 0 | 1 | null;
  plan_eligible?: boolean | null;
  source?: string | null;
}

export interface VirtualTourCapacity {
  cap: number;
  active: number;
  archived: number;
  other_states: number;
  utilisation: number;
  free_reactivations: number;
  renewal_date: string | null;
  reclaimable: {
    on_non_live_listings: VirtualTourModel[];
    unlinked: VirtualTourModel[];
    count: number;
  };
}

export interface LinkedWithoutAssetListing {
  eid: number;
  external_id: string | null;
  listing_name: string | null;
  listing_uuid: string | null;
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const virtualTourKeys = {
  all: ['admin-virtual-tours'] as const,
  overview: (scope: string) => [...virtualTourKeys.all, 'overview', scope] as const,
  list: (params: object) => [...virtualTourKeys.all, 'list', params] as const,
  detail: (vtid: number) => [...virtualTourKeys.all, 'detail', vtid] as const,
  capacity: (refresh: boolean) => [...virtualTourKeys.all, 'capacity', refresh] as const,
  inventory: (params: object) => [...virtualTourKeys.all, 'inventory', params] as const,
  events: (params: object) => [...virtualTourKeys.all, 'events', params] as const,
  runs: (limit: number) => [...virtualTourKeys.all, 'runs', limit] as const,
  linkedWithoutAsset: () => [...virtualTourKeys.all, 'linked-without-asset'] as const,
  reactivations: () => [...virtualTourKeys.all, 'reactivations'] as const,
  listing: (eid: number) => [...virtualTourKeys.all, 'listing', eid] as const,
  media: (vtid: number) => [...virtualTourKeys.all, 'media', vtid] as const,
  addons: (vtid: number) => [...virtualTourKeys.all, 'addons', vtid] as const,
  reclaim: (scope: string) => [...virtualTourKeys.all, 'reclaim', scope] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

const useReq = () => useClientStore((s) => s.request);

export const useVirtualTourOverviewQuery = (scope: string) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.overview(scope),
    queryFn: withErrorHandling(
      async () => (unwrap(await request<any>(`${BASE}/overview${buildQs({ scope })}`)) ?? {}).overview as VirtualTourOverview,
      'Failed to load the virtual tours overview'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export interface VirtualTourListParams {
  status?: string;
  provider?: string;
  source?: string;
  eligibility?: string;
  q?: string;
  eid?: number;
  scope?: string;
  page?: number;
  page_size?: number;
}

export interface VirtualTourListResponse {
  tours: VirtualTour[];
  total: number;
  page: number;
  page_size: number;
}

export const useVirtualToursQuery = (params: VirtualTourListParams = {}) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.list(params),
    queryFn: withErrorHandling(
      async () => unwrap(await request<any>(`${BASE}${buildQs(params as Record<string, unknown>)}`)) as VirtualTourListResponse,
      'Failed to load virtual tours'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useVirtualTourDetailQuery = (vtid: number | null) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.detail(vtid ?? -1),
    enabled: vtid != null,
    queryFn: withErrorHandling(
      async () => (unwrap(await request<any>(`${BASE}/${vtid}`)) ?? {}).tour as VirtualTourDetail,
      'Failed to load this tour'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

/**
 * Capacity meter + reclaimable spaces. 502 `provider_unavailable` when
 * Matterport can't be reached — callers check `getApiErrorCode(error)` and
 * render an inline "Matterport unreachable" note instead of a hard failure.
 */
export const useVirtualTourCapacityQuery = (refresh = false) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.capacity(refresh),
    queryFn: withErrorHandling(
      async () =>
        (unwrap(await request<any>(`${BASE}/capacity${buildQs({ refresh: refresh ? 1 : undefined })}`)) ?? {})
          .capacity as VirtualTourCapacity,
      'Failed to load capacity'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};

export interface VirtualTourInventoryParams {
  link?: 'linked' | 'unlinked';
  state?: string;
  q?: string;
  refresh?: boolean;
}

export const useVirtualTourInventoryQuery = (params: VirtualTourInventoryParams = {}) => {
  const request = useReq();
  const qsParams = { ...params, refresh: params.refresh ? 1 : undefined };
  return useQuery({
    queryKey: virtualTourKeys.inventory(params),
    queryFn: withErrorHandling(
      async () =>
        unwrap(await request<any>(`${BASE}/inventory${buildQs(qsParams)}`)) as {
          models: VirtualTourModel[];
          total: number;
        },
      'Failed to load the Matterport inventory'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export interface VirtualTourEventsParams {
  outcome?: string;
  op?: string;
  eid?: number;
  vtid?: number;
  run_id?: string;
  hours?: number;
  page?: number;
  page_size?: number;
}

export interface VirtualTourEventsResponse {
  events: VirtualTourEvent[];
  total: number;
  by_code: { code: string; n: number }[];
  page: number;
  page_size: number;
}

export const useVirtualTourEventsQuery = (params: VirtualTourEventsParams = {}) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.events(params),
    queryFn: withErrorHandling(
      async () => unwrap(await request<any>(`${BASE}/events${buildQs(params as Record<string, unknown>)}`)) as VirtualTourEventsResponse,
      'Failed to load events'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useVirtualTourRunsQuery = (limit = 50) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.runs(limit),
    queryFn: withErrorHandling(
      async () => (unwrap(await request<any>(`${BASE}/runs${buildQs({ limit })}`)) ?? {}).runs as VirtualTourRun[],
      'Failed to load sync runs'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useVirtualTourLinkedWithoutAssetQuery = () => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.linkedWithoutAsset(),
    queryFn: withErrorHandling(
      async () =>
        unwrap(await request<any>(`${BASE}/linked-without-asset`)) as {
          listings: LinkedWithoutAssetListing[];
          total: number;
        },
      'Failed to load listings linked without an asset'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export interface VirtualTourReactivations {
  free: number;
  used: number;
  remaining: number;
}

export const useVirtualTourReactivationsQuery = () => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.reactivations(),
    queryFn: withErrorHandling(
      async () => (unwrap(await request<any>(`${BASE}/reactivations`)) ?? {}).reactivations as VirtualTourReactivations,
      'Failed to load reactivations'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};

export interface VirtualTourAllowance {
  used: number;
  /** -1 = unlimited. */
  limit: number;
  can_add: boolean;
}

export const useVirtualTourListingQuery = (eid: number | null, enabled = true) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.listing(eid ?? -1),
    enabled: enabled && eid != null && Number.isFinite(eid),
    queryFn: withErrorHandling(
      async () => unwrap(await request<any>(`${BASE}/listings/${eid}`)) as { tours: VirtualTour[]; allowance: VirtualTourAllowance },
      'Failed to load this listing\'s virtual tours'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};

// ─── Media / add-ons / reclaim ───────────────────────────────────────────────

export interface TourMediaSnapshot {
  id: string;
  label: string | null;
  filename: string | null;
  format: string | null;
  width: number | null;
  height: number | null;
  thumb: string | null;
  created: string | null;
}

export interface TourMediaFloorplan {
  id: string;
  format: string | null;
  flags: string[] | string | null;
  colored: boolean | null;
  importable: boolean;
}

export interface TourDimensionRoom {
  room: string | null;
  dimensions: string | null;
  ceiling_height: string | number | null;
  net_floor_area: string | number | null;
  gross_floor_area: string | number | null;
  floor: string | null;
}

export interface TourDimensions {
  model_name: string | null;
  units: string | null;
  floors: {
    floor_label: string | null;
    net_floor_area: string | number | null;
    gross_floor_area: string | number | null;
    rooms: TourDimensionRoom[];
  }[];
  totals: { label: string | null; net_floor_area: string | number | null; gross_floor_area: string | number | null } | null;
}

export interface TourMedia {
  model_id: string;
  eid: number | null;
  snapshots: TourMediaSnapshot[];
  floorplans: TourMediaFloorplan[];
  errors: { snapshots?: string; floorplans?: string };
  dimensions: TourDimensions | null;
  /** limit -1 = unlimited. */
  images: { used: number; limit: number };
  entitlements: { auto_floor_plan: boolean; auto_floor_plan_colored: boolean };
  max_import_items: number;
}

/** Lazy: pass `enabled=true` only once the Media section is expanded. */
export const useVirtualTourMediaQuery = (vtid: number | null, enabled: boolean) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.media(vtid ?? -1),
    enabled: enabled && vtid != null,
    queryFn: withErrorHandling(
      async () => (unwrap(await request<any>(`${BASE}/${vtid}/media`)) ?? {}).media as TourMedia,
      'Failed to load media'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};

export interface TourAddonBundle {
  id: string;
  name: string | null;
  description: string | null;
  availability: string | null;
  processing_status: string | null;
  failure_reason: string | null;
  assets: { filename: string | null; format: string | null; status: string | null; url: string | null }[];
}

export interface TourAddons {
  model_id: string;
  bundles: TourAddonBundle[];
  orders: { cap: number; used: number; remaining: number };
}

export const useVirtualTourAddonsQuery = (vtid: number | null, enabled: boolean) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.addons(vtid ?? -1),
    enabled: enabled && vtid != null,
    queryFn: withErrorHandling(
      async () => (unwrap(await request<any>(`${BASE}/${vtid}/addons`)) ?? {}).addons as TourAddons,
      'Failed to load add-ons'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};

export interface ReclaimCandidate {
  vtid: number;
  eid: number | null;
  model_id: string | null;
  listing_name: string | null;
  tester: number | null;
  reason: 'listing_not_live' | 'no_eligible_plan' | string;
  exempt: boolean;
  ineligible_since: string | null;
  due_at: string | null;
  due: boolean;
}

export interface TourReclaim {
  dry_run: boolean;
  enabled: boolean;
  grace_days: number;
  max_per_run: number;
  due: number;
  exempt: number;
  candidates: ReclaimCandidate[];
  /** vtid lists on a real run. */
  reclaimed: number[];
  failed: number[];
}

export const useVirtualTourReclaimQuery = (scope: string) => {
  const request = useReq();
  return useQuery({
    queryKey: virtualTourKeys.reclaim(scope),
    queryFn: withErrorHandling(
      async () => (unwrap(await request<any>(`${BASE}/reclaim${buildQs({ scope })}`)) ?? {}).reclaim as TourReclaim,
      'Failed to load slot reclaim'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};
